import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  School as TeachIcon,
  Send as SendIcon,
  Tune as TuneIcon,
  ArrowBack as BackIcon,
  AttachFile as AttachIcon,
  CheckCircleOutline as ApproveIcon,
  Public as WebIcon,
  Sms as SmsIcon,
  EditNote as TaskChangeIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import {
  staffAiApi,
  staffAiError,
  StaffAiConversation,
  StaffAiMessage,
  StaffAiPatient,
  StaffAiPatientHit,
  StaffAiPatientOverview,
  StaffAiPatientThread,
  StaffAiPreferences,
  StaffAiStatus,
  StaffAiTeachStatus,
} from '../services/staffAiApi';
import { triageApi } from '../services/api';
import VoiceTools, { SpeakButton } from '../components/common/VoiceTools';
import AppointmentModal from '../components/Calendar/AppointmentModal';
import TaskDetailsModal from '../components/Dashboard/TaskDetailsModal';
import { parseTimeframeToDate } from '../components/PatientDetails/scheduling';
import {
  ActionButtons,
  CHAT_PROMPTS,
  DraftBox,
  OpenTaskBanner,
  PATIENT_PROMPTS,
  QuickPrompts,
  SendToPatientDialog,
  parseAnswer,
} from '../components/AskAi/PatientTools';

type Mode = 'general' | 'teach' | 'patient';

const fmt = (s?: string | null) => (s ? new Date(s).toLocaleString() : '');

const UPLOAD_PREVIEW = 700;

const teachKeys = (m: StaffAiMessage): string[] =>
  m.role === 'assistant' && m.meta?.teach ? (m.meta.teach.keys || [m.meta.teach.key]).filter(Boolean) : [];

const STATUS_LABEL: Record<string, string> = { draft: 'Waiting for review', approved: 'Approved', rejected: 'Rejected', retired: 'Retired' };

interface PatientMessageTools {
  lmdId: string;
  threadId: string | null;
  taskId: string | null;
  canText: boolean;
  onReview: (draft: string) => void;
  onBook: (text: string) => void;
  onChanged: () => void;
}

const Linkified: React.FC<{ text: string; onOpen?: (lmdId: string) => void }> = ({ text, onOpen }) => {
  if (!onOpen) return <>{text}</>;
  const parts = text.split(/\b(LMD-\d{3,})\b/);
  return (
    <>
      {parts.map((p, i) => (i % 2 ? (
        <Link key={i} component="button" title="Open this patient in the Patient tab" onClick={() => onOpen(p)} sx={{ verticalAlign: 'baseline', fontSize: 'inherit', fontFamily: 'inherit' }}>{p}</Link>
      ) : <React.Fragment key={i}>{p}</React.Fragment>))}
    </>
  );
};

const MessageBubble: React.FC<{
  m: StaffAiMessage;
  statuses: Record<string, StaffAiTeachStatus>;
  approving: boolean;
  onApprove: (keys: string[]) => void;
  tools?: PatientMessageTools;
  onOpenPatient?: (lmdId: string) => void;
}> = ({ m, statuses, approving, onApprove, tools, onOpenPatient }) => {
  const [expanded, setExpanded] = useState(false);
  const mine = m.role === 'user' || m.role === 'teach' || m.role === 'sent';
  const offLabel = m.role === 'assistant' && (m.meta?.fallback === 'human' || m.content === 'AI off: needs a person');
  const parsed = m.role === 'assistant' && tools && !offLabel ? parseAnswer(m.content) : null;
  const keys = teachKeys(m);
  const states = keys.map((k) => statuses[k]?.status || (m.meta?.teach?.approved ? 'approved' : 'draft'));
  const drafts = keys.filter((_, i) => states[i] === 'draft');
  const overall = !keys.length ? '' : states.every((s) => s === 'approved') ? 'approved' : drafts.length ? 'draft' : states.find((s) => s !== 'approved') || '';
  const web: { title: string; url: string }[] = (m.role === 'assistant' && m.meta?.sources?.web) || [];
  const longUpload = mine && m.content.startsWith('Uploaded document:') && m.content.length > UPLOAD_PREVIEW;
  const shown = longUpload && !expanded ? `${m.content.slice(0, UPLOAD_PREVIEW)}…` : m.content;
  return (
    <Box sx={{ display: 'flex', justifyContent: m.role === 'action' ? 'center' : mine ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1.25,
          maxWidth: m.role === 'action' ? '70%' : '80%',
          bgcolor: m.role === 'teach' ? '#fff7e6' : m.role === 'sent' ? '#e8f5e9' : m.role === 'action' ? '#f3f4f6' : mine ? '#e8f1fb' : offLabel ? '#fdecea' : '#ffffff',
          borderColor: m.role === 'teach' ? '#f5c46b' : m.role === 'sent' ? '#a5d6a7' : undefined,
        }}
      >
        {m.role === 'teach' && (
          <Chip size="small" icon={<TeachIcon />} label="Teach entry" sx={{ mb: 0.75 }} color="warning" variant="outlined" />
        )}
        {m.role === 'sent' && (
          <Chip size="small" icon={<SmsIcon />} label="Texted to the patient" sx={{ mb: 0.75 }} color="success" variant="outlined" />
        )}
        {m.role === 'action' && (
          <Chip size="small" icon={<TaskChangeIcon />} label="Triage task changed" sx={{ mb: 0.75 }} variant="outlined" />
        )}
        {parsed ? (
          <>
            {parsed.body && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{parsed.body}</Typography>}
            {parsed.draft && <DraftBox draft={parsed.draft} canSend={tools!.canText} onReview={() => tools!.onReview(parsed.draft!)} />}
            <ActionButtons
              actions={parsed.actions}
              scheduling={parsed.scheduling}
              lmdId={tools!.lmdId}
              taskId={tools!.taskId}
              threadId={tools!.threadId}
              onDone={tools!.onChanged}
              onBook={tools!.onBook}
            />
          </>
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {m.role === 'assistant' ? <Linkified text={shown} onOpen={onOpenPatient} /> : shown}
          </Typography>
        )}
        {longUpload && (
          <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => setExpanded((x) => !x)}>
            {expanded ? 'Show less' : 'Show the whole document'}
          </Button>
        )}
        {web.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Web sources looked at:</Typography>
            {web.map((w, i) => (
              <Typography key={w.url} variant="caption" sx={{ display: 'block', wordBreak: 'break-word' }}>
                [{i + 1}] <Link href={w.url} target="_blank" rel="noopener noreferrer">{w.title}</Link>
              </Typography>
            ))}
          </Box>
        )}
        {keys.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={keys.length > 1 ? `${STATUS_LABEL[overall] || overall} (${keys.length} entries)` : STATUS_LABEL[overall] || overall}
              color={overall === 'approved' ? 'success' : overall === 'draft' ? 'default' : 'error'}
              variant={overall === 'approved' ? 'filled' : 'outlined'}
            />
            {drafts.length > 0 && (
              <Tooltip title="Approve your own entry now. It starts shaping answers right away and shows as approved in Skills review under your name. A Levitan admin can roll it back.">
                <span>
                  <Button size="small" variant="contained" color="success" startIcon={<ApproveIcon />} disabled={approving} onClick={() => onApprove(drafts)}>
                    {approving ? 'Approving…' : drafts.length > 1 ? `Approve all ${drafts.length}` : 'Approve'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {fmt(m.createdAt)}
            {m.meta?.viaFallback ? ' · answered by the fallback' : ''}
          </Typography>
          {m.role === 'assistant' && !offLabel && <SpeakButton text={m.content} tip="Read this answer aloud. Click again to stop." />}
        </Box>
      </Paper>
    </Box>
  );
};

const AskLevitanAI: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState<StaffAiStatus | null>(null);
  const [statusError, setStatusError] = useState('');
  const [needsUnlock, setNeedsUnlock] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');

  const [mode, setMode] = useState<Mode>('general');
  const [conversations, setConversations] = useState<StaffAiConversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<StaffAiMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [patientQuery, setPatientQuery] = useState('');
  const [patientHits, setPatientHits] = useState<StaffAiPatientHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [patient, setPatient] = useState<StaffAiPatient | null>(null);
  const [threads, setThreads] = useState<StaffAiPatientThread[]>([]);
  const [activeThread, setActiveThread] = useState<string | null>(null);

  const [draft, setDraft] = useState('');
  const [asTeach, setAsTeach] = useState(false);
  const [teachKind, setTeachKind] = useState<'knowledge' | 'skill'>('knowledge');
  const [sending, setSending] = useState(false);

  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState<StaffAiPreferences>({ voice: '', length: 'normal', instructions: '' });
  const [showList, setShowList] = useState(true);
  const endRef = useRef<HTMLDivElement | null>(null);

  const [statuses, setStatuses] = useState<Record<string, StaffAiTeachStatus>>({});
  const [approvingKey, setApprovingKey] = useState<string | null>(null);
  const [webOn, setWebOn] = useState(() => localStorage.getItem('staffAiWeb') !== 'off');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadAsTeach, setUploadAsTeach] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [link, setLink] = useState<{ patient?: string; task?: string; find?: string } | null>(null);
  const [overview, setOverview] = useState<StaffAiPatientOverview | null>(null);
  const [focusTaskId, setFocusTaskId] = useState<string | null>(null);
  const [compose, setCompose] = useState<{ text: string; aiDraft?: string } | null>(null);
  const [booking, setBooking] = useState<string | null>(null);
  const [openTask, setOpenTask] = useState<any | null>(null);

  const fail = useCallback((e: any) => {
    const err = staffAiError(e);
    if (err.code === 'authenticator_required') {
      staffAiApi.clearUnlock();
      setNeedsUnlock(true);
      return;
    }
    enqueueSnackbar(err.message, { variant: 'error' });
  }, [enqueueSnackbar]);

  const loadConversations = useCallback(async () => {
    try {
      setConversations(await staffAiApi.listConversations());
    } catch (e) {
      fail(e);
    }
  }, [fail]);

  useEffect(() => {
    staffAiApi.status()
      .then((s) => {
        setStatus(s);
        loadConversations();
      })
      .catch((e) => setStatusError(staffAiError(e).message));
  }, [loadConversations]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const mergeStatuses = (rows: StaffAiTeachStatus[]) =>
    setStatuses((s) => ({ ...s, ...Object.fromEntries(rows.map((r) => [r.key, r])) }));

  const keysInView = messages.flatMap(teachKeys).join(',');
  useEffect(() => {
    if (!keysInView) return;
    staffAiApi.teachStatus(keysInView.split(',')).then(mergeStatuses).catch(() => undefined);
  }, [keysInView]);

  const approveKeys = async (keys: string[]) => {
    if (!keys.length) return;
    setApprovingKey(keys[0]);
    try {
      for (const k of keys) await staffAiApi.approveTeach(k);
      mergeStatuses(await staffAiApi.teachStatus(keys));
      enqueueSnackbar('Approved. It is live now and shows as approved in Skills review under your name.', { variant: 'success' });
    } catch (e) {
      fail(e);
      staffAiApi.teachStatus(keys).then(mergeStatuses).catch(() => undefined);
    } finally {
      setApprovingKey(null);
    }
  };

  const teachSnack = (d: any, approve: boolean) => {
    if (!d) return;
    if (!approve) enqueueSnackbar('Saved as a draft for review. You can approve it from the message.', { variant: 'success' });
    else if (d.approved) enqueueSnackbar('Approved. It is live now and shows as approved in Skills review under your name.', { variant: 'success' });
    else enqueueSnackbar('Saved as a draft, but approving did not go through. Try Approve on the message.', { variant: 'warning' });
  };

  const openConversation = async (id: string) => {
    setActiveId(id);
    setLoadingMessages(true);
    if (isMobile) setShowList(false);
    try {
      setMessages(await staffAiApi.getMessages(id));
    } catch (e) {
      fail(e);
    } finally {
      setLoadingMessages(false);
    }
  };

  const newConversation = async () => {
    if (mode === 'patient') return;
    try {
      const c = await staffAiApi.createConversation(mode);
      setConversations((cs) => [c, ...cs]);
      setActiveId(c.id);
      setMessages([]);
      if (isMobile) setShowList(false);
    } catch (e) {
      fail(e);
    }
  };

  const deleteConversation = async (id: string) => {
    if (!window.confirm('Delete this conversation for good?')) return;
    try {
      await staffAiApi.deleteConversation(id);
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
    } catch (e) {
      fail(e);
    }
  };

  const searchPatients = async (q = patientQuery) => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      setPatientHits(await staffAiApi.searchPatients(q.trim()));
    } catch (e) {
      fail(e);
    } finally {
      setSearching(false);
    }
  };

  const openThreadFor = async (lmdId: string, id: string) => {
    setActiveThread(id);
    setLoadingMessages(true);
    if (isMobile) setShowList(false);
    try {
      setMessages(await staffAiApi.getPatientMessages(lmdId, id));
    } catch (e) {
      fail(e);
    } finally {
      setLoadingMessages(false);
    }
  };

  /** Returns false only when the authenticator has to be entered first, so a deep link can retry after unlocking. */
  const openPatientById = async (lmdId: string, taskId?: string): Promise<boolean> => {
    try {
      const r = await staffAiApi.openPatient(lmdId);
      setPatient(r.patient);
      setThreads(r.threads);
      setOverview(r.overview);
      setFocusTaskId(taskId || null);
      setActiveThread(null);
      setMessages([]);
      setPatientHits([]);
      setPatientQuery('');
      if (taskId) {
        const t = await staffAiApi.taskThread(r.patient.lmdId, taskId);
        setThreads((ts) => (ts.some((x) => x.id === t.id) ? ts : [t, ...ts]));
        await openThreadFor(r.patient.lmdId, t.id);
      }
      return true;
    } catch (e) {
      fail(e);
      if (staffAiError(e).code === 'patient_unidentified') {
        setPatientQuery(lmdId);
        searchPatients(lmdId);
      }
      return staffAiError(e).code !== 'authenticator_required';
    }
  };

  const openPatient = async (hit: StaffAiPatientHit) => {
    if (!hit.identifiable) {
      enqueueSnackbar("Can't identify this patient: no Spruce contact is linked to this LMD id.", { variant: 'warning' });
      return;
    }
    await openPatientById(hit.lmdId);
  };

  const closePatient = () => {
    setPatient(null);
    setThreads([]);
    setOverview(null);
    setFocusTaskId(null);
    setActiveThread(null);
    setMessages([]);
  };

  const openThread = async (id: string) => {
    if (!patient) return;
    await openThreadFor(patient.lmdId, id);
  };

  const refreshOverview = useCallback(async () => {
    if (!patient) return;
    try {
      setOverview(await staffAiApi.patientOverview(patient.lmdId));
    } catch {
      /* the banner keeps the last overview */
    }
  }, [patient]);

  const refreshThread = async () => {
    if (!patient || !activeThread) return;
    try {
      setMessages(await staffAiApi.getPatientMessages(patient.lmdId, activeThread));
    } catch (e) {
      fail(e);
    }
  };

  const showTask = async (id: string) => {
    try {
      const res = await triageApi.getTask(id);
      setOpenTask(res.data?.data || res.data?.task || res.data);
    } catch {
      enqueueSnackbar('Could not open that triage task.', { variant: 'error' });
    }
  };

  useEffect(() => {
    const p = searchParams.get('patient') || undefined;
    const find = searchParams.get('find') || undefined;
    if (!p && !find) return;
    setLink({ patient: p, task: searchParams.get('task') || undefined, find });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!link || !status || needsUnlock) return;
    const l = link;
    setLink(null);
    setMode('patient');
    setActiveId(null);
    setAsTeach(false);
    setShowList(true);
    setOpenTask(null);
    if (l.patient) {
      openPatientById(l.patient, l.task).then((ok) => { if (!ok) setLink(l); });
    } else if (l.find) {
      closePatient();
      setPatientQuery(l.find);
      searchPatients(l.find);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, status, needsUnlock]);

  const openPatientFromChat = (lmdId: string) => setLink({ patient: lmdId });

  const newThread = async () => {
    if (!patient) return;
    try {
      const t = await staffAiApi.createPatientThread(patient.lmdId);
      setThreads((ts) => [t, ...ts]);
      setActiveThread(t.id);
      setMessages([]);
      if (isMobile) setShowList(false);
    } catch (e) {
      fail(e);
    }
  };

  const activeConversation = conversations.find((c) => c.id === activeId) || null;
  const teachOnly = mode === 'teach' || activeConversation?.mode === 'teach';
  const canSend = mode === 'patient' ? !!(patient && activeThread) : !!activeId;

  const canUpload = (teachOnly && !!activeId) || (mode === 'patient' && !!patient && !!activeThread);

  const threadTaskId = threads.find((t) => t.id === activeThread)?.taskId || null;
  const currentTask = overview
    ? overview.openTasks.find((t) => t.id === focusTaskId) || overview.openTasks.find((t) => t.id === threadTaskId) || overview.openTasks[0] || null
    : null;
  const patientTools: PatientMessageTools | undefined = mode === 'patient' && patient ? {
    lmdId: patient.lmdId,
    threadId: activeThread,
    taskId: currentTask?.id || null,
    canText: !!overview?.canText,
    onReview: (d) => setCompose({ text: d, aiDraft: d }),
    onBook: setBooking,
    onChanged: () => { refreshThread(); refreshOverview(); },
  } : undefined;

  const send = async (approve = false, preset?: string, opts: { convId?: string; threadId?: string; practice?: boolean } = {}) => {
    const text = (preset ?? draft).trim();
    const convId = opts.convId || activeId;
    const threadId = opts.threadId || activeThread;
    const ready = mode === 'patient' ? !!(patient && threadId) : !!convId;
    if (!text || !ready || sending) return;
    const teach = preset === undefined && (teachOnly || asTeach);
    const local: StaffAiMessage = { id: `local-${Date.now()}`, role: teach ? 'teach' : 'user', content: text, createdAt: new Date().toISOString() };
    setMessages((ms) => [...ms, local]);
    if (preset === undefined) setDraft('');
    setSending(true);
    try {
      if (mode === 'patient' && patient && threadId) {
        const r = await staffAiApi.sendPatientMessage(patient.lmdId, threadId, text, teach, teachKind, teach && approve);
        if (teach) {
          setMessages((ms) => [...ms, r.assistantMessage]);
          teachSnack(r.teachDraft, approve);
        } else {
          setMessages(await staffAiApi.getPatientMessages(patient.lmdId, threadId));
        }
      } else if (convId) {
        const r = await staffAiApi.sendMessage(convId, text, teach, teachKind, teach && approve, webOn, !!opts.practice);
        setMessages((ms) => [...ms, r.assistantMessage]);
        teachSnack(r.teachDraft, approve);
        loadConversations();
      }
      setAsTeach(false);
    } catch (e) {
      setMessages((ms) => ms.filter((m) => m.id !== local.id));
      if (preset === undefined) setDraft(text);
      fail(e);
    } finally {
      setSending(false);
    }
  };

  const quickChat = async (text: string) => {
    if (sending) return;
    let id = activeConversation?.mode === 'general' ? activeId : null;
    if (!id) {
      try {
        const c = await staffAiApi.createConversation('general');
        setConversations((cs) => [c, ...cs]);
        setActiveId(c.id);
        setMessages([]);
        if (isMobile) setShowList(false);
        id = c.id;
      } catch (e) {
        fail(e);
        return;
      }
    }
    await send(false, text, { convId: id, practice: true });
  };

  const quickPatient = async (text: string) => {
    if (!patient || sending) return;
    let tid = activeThread;
    if (!tid) {
      try {
        const t = await staffAiApi.createPatientThread(patient.lmdId);
        setThreads((ts) => [t, ...ts]);
        setActiveThread(t.id);
        setMessages([]);
        if (isMobile) setShowList(false);
        tid = t.id;
      } catch (e) {
        fail(e);
        return;
      }
    }
    await send(false, text, { threadId: tid });
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      enqueueSnackbar('Files are limited to 50 MB.', { variant: 'warning' });
      return;
    }
    setUploadAsTeach(asTeach);
    setPendingFile(f);
  };

  const upload = async (approve: boolean) => {
    const file = pendingFile;
    if (!file || uploading) return;
    setUploading(true);
    try {
      if (mode === 'patient' && patient && activeThread) {
        const r = await staffAiApi.uploadPatient(patient.lmdId, activeThread, file, { teach: uploadAsTeach, teachKind, approve: uploadAsTeach && approve });
        const fresh = await staffAiApi.getPatientMessages(patient.lmdId, activeThread);
        const d = r.teachDraft;
        setMessages(d ? [...fresh, {
          id: `teach-upload-${Date.now()}`,
          role: 'assistant',
          content: d.approved ? `Teach entries from "${file.name}" are approved.` : `Teach entries from "${file.name}" are waiting for review.`,
          meta: { teach: { key: d.key, keys: d.keys, approved: d.approved } },
          createdAt: new Date().toISOString(),
        }] : fresh);
        enqueueSnackbar(`Added "${file.name}" to this patient's documents and this conversation.`, { variant: 'success' });
        if (d) teachSnack(d, approve);
      } else if (activeId) {
        const r = await staffAiApi.uploadTeach(activeId, file, teachKind, approve);
        setMessages(await staffAiApi.getMessages(activeId));
        loadConversations();
        if (r.teachDraft) teachSnack(r.teachDraft, approve);
        else enqueueSnackbar('No text could be read from that file. It is kept, but nothing went to review.', { variant: 'warning' });
      }
      setPendingFile(null);
    } catch (e) {
      fail(e);
    } finally {
      setUploading(false);
    }
  };

  const toggleWeb = (on: boolean) => {
    setWebOn(on);
    localStorage.setItem('staffAiWeb', on ? 'on' : 'off');
  };

  const openPrefs = async () => {
    try {
      setPrefs(await staffAiApi.getPreferences());
      setPrefsOpen(true);
    } catch (e) {
      fail(e);
    }
  };

  const savePrefs = async () => {
    try {
      setPrefs(await staffAiApi.putPreferences(prefs));
      setPrefsOpen(false);
      enqueueSnackbar('Preferences saved.', { variant: 'success' });
    } catch (e) {
      fail(e);
    }
  };

  const unlock = async () => {
    try {
      await staffAiApi.unlock(unlockCode.trim());
      setNeedsUnlock(false);
      setUnlockCode('');
      loadConversations();
    } catch (e) {
      enqueueSnackbar(staffAiError(e).message, { variant: 'error' });
    }
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setActiveId(null);
    setActiveThread(null);
    setMessages([]);
    setAsTeach(false);
    setShowList(true);
  };

  if (statusError) {
    return <Alert severity="error" sx={{ m: 2 }}>{statusError}</Alert>;
  }
  if (!status) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const listed = conversations.filter((c) => c.mode === mode);
  const headerTitle = mode === 'patient'
    ? (patient ? threads.find((t) => t.id === activeThread)?.title || 'Pick or start a conversation' : 'Pick a patient')
    : activeConversation?.title || (mode === 'teach' ? 'Teach Levitan AI' : 'Ask Levitan AI');

  const leftPanel = (
    <Paper variant="outlined" sx={{ width: isMobile ? '100%' : 320, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Tabs value={mode} onChange={(_, v) => switchMode(v)} variant="fullWidth">
        <Tab value="general" label="Chats" />
        <Tab value="teach" label="Teach" />
        <Tab value="patient" label="Patient" />
      </Tabs>
      <Divider />
      {mode !== 'patient' ? (
        <>
          <Box sx={{ p: 1.5 }}>
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={newConversation} disabled={!status.configured}>
              {mode === 'teach' ? 'New Teach chat' : 'New chat'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {mode === 'teach'
                ? 'This tab is only for you. What you teach here changes how Levitan AI answers you and drafts in your voice. It does not change the AI for anyone else. Clinic-wide teaching is on the admin site.'
                : 'Only you can see your chats. Answers use the practice guidance and, when Search the web is on, public web pages.'}
            </Typography>
          </Box>
          <List dense sx={{ overflowY: 'auto', flex: 1 }}>
            {listed.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>No conversations yet.</Typography>
            )}
            {listed.map((c) => (
              <ListItemButton key={c.id} selected={c.id === activeId} onClick={() => openConversation(c.id)}>
                <ListItemText primary={c.title} secondary={fmt(c.lastMessageAt || c.createdAt)} primaryTypographyProps={{ noWrap: true }} />
                <Tooltip title="Delete">
                  <IconButton edge="end" size="small" onClick={(ev) => { ev.stopPropagation(); deleteConversation(c.id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemButton>
            ))}
          </List>
        </>
      ) : !patient ? (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0, flex: 1 }}>
          <TextField
            size="small"
            label="Name, LMD id or phone"
            value={patientQuery}
            onChange={(e) => setPatientQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') searchPatients(); }}
          />
          <Button variant="outlined" onClick={() => searchPatients()} disabled={searching || patientQuery.trim().length < 2}>
            {searching ? 'Searching…' : 'Find patient'}
          </Button>
          <List dense sx={{ overflowY: 'auto', flex: 1 }}>
            {patientHits.map((h) => (
              <ListItemButton key={h.lmdId} onClick={() => openPatient(h)} disabled={!h.identifiable}>
                <ListItemText
                  primary={`${h.name}${h.archived ? ' (archived)' : ''}`}
                  secondary={[h.lmdId, h.dob ? `DOB ${h.dob}` : '', h.phoneLast4 ? `phone …${h.phoneLast4}` : '', h.identifiable ? '' : "can't identify"].filter(Boolean).join(' · ')}
                />
              </ListItemButton>
            ))}
          </List>
          <Typography variant="caption" color="text.secondary">
            Patient conversations are shared with the care team, like the consultation tab in PT Details. Every patient you open is logged.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ p: 1.5 }}>
            <Button size="small" startIcon={<BackIcon />} onClick={closePatient}>Other patient</Button>
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={newThread} sx={{ mt: 1 }}>New conversation</Button>
          </Box>
          <List dense sx={{ overflowY: 'auto', flex: 1 }}>
            {threads.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>No conversations about this patient yet.</Typography>
            )}
            {threads.map((t) => (
              <ListItemButton key={t.id} selected={t.id === activeThread} onClick={() => openThread(t.id)}>
                <ListItemText primary={t.title} secondary={[t.createdBy, fmt(t.lastMessageAt || t.createdAt)].filter(Boolean).join(' · ')} primaryTypographyProps={{ noWrap: true }} />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    </Paper>
  );

  const chatPanel = (
    <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
        {isMobile && (
          <IconButton size="small" onClick={() => setShowList(true)}><BackIcon /></IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {mode === 'patient' && patient && (
            <Chip
              icon={<PersonIcon />}
              color="primary"
              label={`${patient.name}${patient.dob ? ` · DOB ${patient.dob}` : ''} · ${patient.lmdId}`}
              sx={{ mb: 0.5, maxWidth: '100%' }}
            />
          )}
          <Typography variant="subtitle1" noWrap>{headerTitle}</Typography>
        </Box>
        {mode === 'patient' && patient && (
          <Tooltip title="Locked to this patient: answers use only this patient's record and messages.">
            <LockIcon color="action" fontSize="small" />
          </Tooltip>
        )}
        <Tooltip title="My preferences">
          <IconButton size="small" onClick={openPrefs}><TuneIcon /></IconButton>
        </Tooltip>
      </Box>
      {mode === 'patient' && patient && (
        <OpenTaskBanner
          overview={overview}
          currentTaskId={currentTask?.id}
          onOpenTask={showTask}
          onCompose={() => setCompose({ text: '' })}
        />
      )}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f8fafc' }}>
        {loadingMessages ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
        ) : !canSend ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>
            {mode === 'patient'
              ? (patient ? 'Pick a conversation on the left, start a new one, or tap a quick question below.' : 'Find a patient on the left. The conversation stays on that patient.')
              : mode === 'general'
                ? 'Pick a conversation on the left, start a new one, or tap a quick question below about the practice\'s work right now.'
                : 'Pick a conversation on the left or start a new one.'}
          </Typography>
        ) : messages.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 6 }}>
            {teachOnly
              ? 'Write how the practice handles something, for example: "When a patient asks for a refill early, we …", or upload a document. Save it for review, or approve it yourself.'
              : mode === 'patient'
                ? 'Ask about this patient: history, medications, results, messages, open tasks. Ask for a reply and Levitan AI drafts it; you review it and press Send. You can also upload a document for this patient.'
                : 'Ask about procedures, policies, how we usually answer patients, the practice\'s work right now (who is waiting, today\'s schedule, a shift handoff), or anything general: Search the web looks it up online.'}
          </Typography>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              m={m}
              statuses={statuses}
              approving={!!approvingKey && teachKeys(m).includes(approvingKey)}
              onApprove={approveKeys}
              tools={patientTools}
              onOpenPatient={mode === 'general' ? openPatientFromChat : undefined}
            />
          ))
        )}
        {sending && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {teachOnly || asTeach ? 'Saving…' : mode === 'general' && webOn ? 'Searching the web and thinking…' : 'Levitan AI is thinking…'}
            </Typography>
          </Box>
        )}
        <div ref={endRef} />
      </Box>
      <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
        {mode === 'patient' && patient && !asTeach && (
          <QuickPrompts items={PATIENT_PROMPTS.map((t) => ({ label: t, text: t }))} disabled={sending || !status.configured} onPick={quickPatient} />
        )}
        {mode === 'general' && !teachOnly && !asTeach && (
          <QuickPrompts items={CHAT_PROMPTS} disabled={sending || !status.configured} onPick={quickChat} />
        )}
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          placeholder={teachOnly || asTeach ? 'Teach entry (no patient names, phone numbers or dates of birth)' : 'Write a message'}
          value={draft}
          disabled={!canSend || sending}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {!teachOnly && (
            <FormControlLabel
              control={<Checkbox size="small" checked={asTeach} onChange={(e) => setAsTeach(e.target.checked)} disabled={!canSend} />}
              label={<Typography variant="body2">Send as a Teach entry (save for review or approve)</Typography>}
            />
          )}
          {(teachOnly || asTeach) && (
            <TextField select size="small" value={teachKind} onChange={(e) => setTeachKind(e.target.value as any)} sx={{ minWidth: 200 }}>
              <MenuItem value="knowledge">Practice knowledge</MenuItem>
              <MenuItem value="skill">How we answer (skill)</MenuItem>
            </TextField>
          )}
          {mode === 'general' && !teachOnly && !asTeach && (
            <Tooltip title="Also look up public web pages for this question. Names, phone numbers and dates are removed before anything is searched. Answers list the pages they used.">
              <FormControlLabel
                control={<Switch size="small" checked={webOn} onChange={(e) => toggleWeb(e.target.checked)} disabled={!canSend} />}
                label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><WebIcon fontSize="small" color="action" /><Typography variant="body2">Search the web</Typography></Box>}
              />
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {canUpload && (
            <Tooltip title={mode === 'patient'
              ? "Upload a document for this patient (PDF, Word, Excel, PowerPoint, text, scans, photos, audio or video; up to 50 MB). It goes into the patient's documents and this conversation."
              : 'Upload a document (PDF, Word, Excel, PowerPoint, text, scans, photos, audio or video; up to 50 MB). Its text becomes Teach entries.'}>
              <span>
                <IconButton onClick={() => fileRef.current?.click()} disabled={sending || uploading} aria-label="Upload a document">
                  <AttachIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <input ref={fileRef} type="file" hidden onChange={onFilePicked} />
          <VoiceTools value={draft} onChange={setDraft} disabled={!canSend || sending} speakTip="Read your message aloud. Click again to stop." />
          {teachOnly || asTeach ? (
            <>
              <Button variant="outlined" endIcon={<SendIcon />} onClick={() => send(false)} disabled={!canSend || sending || !draft.trim()}>
                Save for review
              </Button>
              <Tooltip title="Save and approve your entry now. It starts shaping answers right away and shows as approved in Skills review under your name.">
                <span>
                  <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => send(true)} disabled={!canSend || sending || !draft.trim()}>
                    Approve
                  </Button>
                </span>
              </Tooltip>
            </>
          ) : (
            <Button variant="contained" endIcon={<SendIcon />} onClick={() => send(false)} disabled={!canSend || sending || !draft.trim()}>
              Send
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { xs: 'calc(100vh - 140px)', md: 'calc(100vh - 112px)' }, gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Ask Levitan AI</Typography>
        <Chip size="small" label="Runs on our own AI" variant="outlined" />
      </Box>
      {!status.on && (
        <Alert severity="warning">AI off: needs a person. Ask Levitan AI is switched off in the AI engine settings.</Alert>
      )}
      {!status.configured && (
        <Alert severity="error">Ask Levitan AI is not set up on this server yet.</Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minHeight: 0 }}>
        {(!isMobile || showList) && leftPanel}
        {(!isMobile || !showList) && chatPanel}
      </Box>

      <Dialog open={prefsOpen} onClose={() => setPrefsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>My preferences</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary">
            Used only in your own chats and drafts. The practice rules always apply on top.
          </Typography>
          <TextField select label="Write patient messages as" value={prefs.voice} onChange={(e) => setPrefs({ ...prefs, voice: e.target.value })}>
            <MenuItem value="">No preference</MenuItem>
            {status.voices.map((v) => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
          </TextField>
          <TextField select label="Answer length" value={prefs.length} onChange={(e) => setPrefs({ ...prefs, length: e.target.value as any })}>
            <MenuItem value="short">Short</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="detailed">Detailed</MenuItem>
          </TextField>
          <TextField
            label="Anything else Levitan AI should keep in mind for you"
            multiline
            minRows={3}
            value={prefs.instructions}
            onChange={(e) => setPrefs({ ...prefs, instructions: e.target.value })}
            inputProps={{ maxLength: 2000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrefsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePrefs}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!pendingFile} onClose={() => !uploading && setPendingFile(null)} fullWidth maxWidth="sm">
        <DialogTitle>Upload a document</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
            {pendingFile?.name} ({pendingFile ? `${Math.max(1, Math.round(pendingFile.size / 1024)).toLocaleString()} KB` : ''})
          </Typography>
          {mode === 'patient' ? (
            <>
              <Typography variant="body2">
                It goes into {patient?.name || 'this patient'}'s documents and into this conversation, so you can ask Levitan AI about it.
              </Typography>
              <FormControlLabel
                control={<Checkbox size="small" checked={uploadAsTeach} onChange={(e) => setUploadAsTeach(e.target.checked)} disabled={uploading} />}
                label={<Typography variant="body2">Also send it as a Teach entry (the patient's name, phone numbers and dates are removed)</Typography>}
              />
            </>
          ) : (
            <Typography variant="body2">
              Its text is read and saved as Teach entries ({teachKind === 'skill' ? 'how we answer' : 'practice knowledge'}), with names, phone numbers and dates removed. Long documents become several entries.
            </Typography>
          )}
          {(mode !== 'patient' || uploadAsTeach) && (
            <TextField select size="small" label="Teach entry type" value={teachKind} onChange={(e) => setTeachKind(e.target.value as any)} disabled={uploading} sx={{ maxWidth: 260 }}>
              <MenuItem value="knowledge">Practice knowledge</MenuItem>
              <MenuItem value="skill">How we answer (skill)</MenuItem>
            </TextField>
          )}
          {uploading && (
            <Box>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">Reading the file… scans and recordings can take a few minutes.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingFile(null)} disabled={uploading}>Cancel</Button>
          {mode === 'patient' && !uploadAsTeach ? (
            <Button variant="contained" startIcon={<AttachIcon />} onClick={() => upload(false)} disabled={uploading}>Add to this patient</Button>
          ) : (
            <>
              <Button variant="outlined" onClick={() => upload(false)} disabled={uploading}>
                {mode === 'patient' ? 'Add and save for review' : 'Save for review'}
              </Button>
              <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={() => upload(true)} disabled={uploading}>
                {mode === 'patient' ? 'Add and approve' : 'Approve'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <SendToPatientDialog
        open={!!compose}
        onClose={() => setCompose(null)}
        overview={overview}
        threadId={activeThread}
        initialText={compose?.text || ''}
        aiDraft={compose?.aiDraft}
        defaultTaskId={currentTask?.id}
        languages={status.languages || []}
        onSent={(r) => {
          setCompose(null);
          setFocusTaskId(r.taskId);
          if (r.sentMessage) setMessages((ms) => [...ms, r.sentMessage!]);
          refreshOverview();
        }}
      />

      {booking && overview && (
        <AppointmentModal
          open
          appointment={null}
          onClose={() => setBooking(null)}
          onSave={(a) => {
            setBooking(null);
            enqueueSnackbar(`Appointment saved for ${a?.patientName || overview.patient.name}.`, { variant: 'success' });
          }}
          initialPatient={{
            name: overview.patient.name,
            phone: overview.patient.phone || '',
            email: overview.patient.email || undefined,
            internalId: overview.patient.lmdId,
          }}
          initialDraft={{ date: parseTimeframeToDate(booking), presentingComplaints: `AI scheduling suggestion: ${booking}`, aiAssisted: true }}
          draggable
        />
      )}

      {openTask && (
        <TaskDetailsModal
          key={String(openTask.id)}
          open
          task={openTask}
          onClose={() => { setOpenTask(null); refreshOverview(); }}
          onUpdate={(t) => setOpenTask(t)}
        />
      )}

      <Dialog open={needsUnlock} onClose={() => setNeedsUnlock(false)}>
        <DialogTitle>Authenticator code</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the 6-digit code from your authenticator app to use Ask Levitan AI.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            value={unlockCode}
            onChange={(e) => setUnlockCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => { if (e.key === 'Enter') unlock(); }}
            inputProps={{ inputMode: 'numeric' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNeedsUnlock(false)}>Cancel</Button>
          <Button variant="contained" onClick={unlock} disabled={unlockCode.length !== 6}>Unlock</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AskLevitanAI;
